import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import {
  Plus, Edit, Phone, Loader2, UserX, UserCheck, Users,
  Clock, ChevronDown, ChevronUp, Trash2, CalendarDays, Building2,
} from 'lucide-react';
import {
  getEmployees, addEmployee, updateEmployee, deleteEmployee,
  getAttendance, addAttendance, updateAttendance, deleteAttendance,
} from '../services/adminService';
import { useAuth } from '../context/AuthContext';
import { useHotelFeatures } from '../context/HotelFeaturesContext';
import { Navigate } from 'react-router-dom';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const EMPTY_EMP  = { fullName: '', department: '', phone: '', joinDate: '' };
const EMPTY_ATT  = { workDate: '', hoursWorked: '', notes: '' };

const DEPT_COLORS = [
  'bg-blue-600','bg-purple-600','bg-green-600','bg-amber-600',
  'bg-rose-600','bg-cyan-600','bg-indigo-600','bg-teal-600',
];
const deptColor = (() => {
  const map = {};
  let i = 0;
  return (dept) => { if (!map[dept]) { map[dept] = DEPT_COLORS[i++ % DEPT_COLORS.length]; } return map[dept]; };
})();

/* ─────────────────────────────────────────────────────────────
   Attendance Panel (per employee, expandable)
───────────────────────────────────────────────────────────── */
const AttendancePanel = ({ employee, isHead }) => {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog]   = useState(null); // { editRec? }
  const [form, setForm]       = useState(EMPTY_ATT);
  const [saving, setSaving]   = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try { setRecords(await getAttendance(employee.id, year, month)); }
    catch { toast({ title: 'Failed to load attendance', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [employee.id, year, month, toast]);

  useEffect(() => { load(); }, [load]);

  const totalHours = records.reduce((s, r) => s + Number(r.hoursWorked), 0);
  const years = Array.from({ length: 3 }, (_, i) => now.getFullYear() - i);

  const openAdd  = () => { setForm(EMPTY_ATT); setDialog({}); };
  const openEdit = (r) => { setForm({ workDate: r.workDate, hoursWorked: String(r.hoursWorked), notes: r.notes || '' }); setDialog({ editRec: r }); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { workDate: form.workDate, hoursWorked: parseFloat(form.hoursWorked), notes: form.notes || null };
      if (dialog.editRec) {
        await updateAttendance(dialog.editRec.id, payload);
        toast({ title: 'Attendance updated' });
      } else {
        await addAttendance(employee.id, payload);
        toast({ title: 'Attendance added' });
      }
      setDialog(null);
      await load();
    } catch (err) { toast({ title: err.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this attendance record?')) return;
    try { await deleteAttendance(id); toast({ title: 'Deleted' }); await load(); }
    catch (err) { toast({ title: err.message, variant: 'destructive' }); }
  };

  return (
    <div className="border-t border-slate-700 bg-slate-900/30">
      {/* Controls */}
      <div className="px-4 py-3 flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <Select value={String(month)} onValueChange={v => setMonth(Number(v))}>
            <SelectTrigger className="w-32 h-7 text-xs bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)} className="text-xs">{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
            <SelectTrigger className="w-24 h-7 text-xs bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              {years.map(y => <SelectItem key={y} value={String(y)} className="text-xs">{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-400 ml-1">
            <Clock className="inline h-3 w-3 mr-1" />{totalHours.toFixed(1)} hrs
          </span>
        </div>
        {isHead && (
          <Button size="sm" onClick={openAdd} className="bg-blue-600 hover:bg-blue-700 h-7 text-xs">
            <Plus className="h-3 w-3 mr-1" />Add
          </Button>
        )}
      </div>

      {/* Records */}
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-blue-400" /></div>
      ) : records.length === 0 ? (
        <p className="text-center text-slate-500 text-sm py-6">No attendance records for {MONTHS[month-1]} {year}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700/60">
              <th className="text-left text-xs text-slate-400 font-medium px-4 py-2">Date</th>
              <th className="text-right text-xs text-slate-400 font-medium px-4 py-2">Hours</th>
              <th className="text-left text-xs text-slate-400 font-medium px-4 py-2">Notes</th>
              {isHead && <th className="px-4 py-2 w-16" />}
            </tr>
          </thead>
          <tbody>
            {records.map((r, i) => (
              <tr key={r.id} className={`border-b border-slate-700/30 ${i % 2 !== 0 ? 'bg-slate-800/20' : ''}`}>
                <td className="px-4 py-2 text-slate-300 text-xs">{r.workDate}</td>
                <td className="px-4 py-2 text-right text-white font-medium text-xs">{r.hoursWorked}</td>
                <td className="px-4 py-2 text-slate-400 text-xs max-w-[160px] truncate">{r.notes || '—'}</td>
                {isHead && (
                  <td className="px-4 py-2">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => openEdit(r)} className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-700"><Edit className="h-3 w-3" /></button>
                      <button onClick={() => handleDelete(r.id)} className="text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-600/20"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-600">
              <td className="px-4 py-2 text-slate-400 text-xs font-medium">Total</td>
              <td className="px-4 py-2 text-right text-white font-bold text-xs">{totalHours.toFixed(1)}</td>
              <td colSpan={isHead ? 2 : 1} />
            </tr>
          </tfoot>
        </table>
      )}

      {/* Add/Edit Attendance Dialog */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-sm bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-blue-400" />
              {dialog?.editRec ? 'Edit Attendance' : 'Add Attendance'}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">{employee.fullName}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={form.workDate} onChange={e => setForm(p => ({ ...p, workDate: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white" required />
            </div>
            <div className="space-y-1.5">
              <Label>Hours Worked</Label>
              <Input type="number" min="0.5" max="24" step="0.5" value={form.hoursWorked}
                onChange={e => setForm(p => ({ ...p, hoursWorked: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white" placeholder="e.g. 8 or 4.5" required />
            </div>
            <div className="space-y-1.5">
              <Label>Notes <span className="text-slate-500 text-xs font-normal">(optional)</span></Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white" placeholder="e.g. Morning shift" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)} className="border-slate-600">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                {dialog?.editRec ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main HR Page
───────────────────────────────────────────────────────────── */
const HR = () => {
  const { user } = useAuth();
  const { isHREnabled, featuresLoading } = useHotelFeatures();
  const isHead = user?.role === 'HotelHead' || user?.role === 'Admin';

  if (!featuresLoading && !isHREnabled) return <Navigate to="/dashboard" replace />;

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [expanded, setExpanded]   = useState(null);
  const [deptFilter, setDeptFilter] = useState('All');

  const [isFormOpen, setIsFormOpen]     = useState(false);
  const [editingEmp, setEditingEmp]     = useState(null);
  const [form, setForm]                 = useState(EMPTY_EMP);

  const { toast } = useToast();

  const load = useCallback(async () => {
    try { setEmployees(await getEmployees()); }
    catch { toast({ title: 'Failed to load employees', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const setField = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const openAdd  = () => { setEditingEmp(null); setForm(EMPTY_EMP); setIsFormOpen(true); };
  const openEdit = (e) => {
    setEditingEmp(e);
    setForm({ fullName: e.fullName, department: e.department, phone: e.phone || '', joinDate: e.joinDate || '' });
    setIsFormOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { fullName: form.fullName, department: form.department, phone: form.phone || null, joinDate: form.joinDate || null };
      if (editingEmp) {
        await updateEmployee(editingEmp.id, { ...payload, isActive: editingEmp.isActive });
        toast({ title: 'Employee updated' });
      } else {
        await addEmployee(payload);
        toast({ title: 'Employee added' });
      }
      setIsFormOpen(false);
      await load();
    } catch (err) { toast({ title: err.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (emp) => {
    try {
      await updateEmployee(emp.id, {
        fullName: emp.fullName, department: emp.department, phone: emp.phone,
        joinDate: emp.joinDate, isActive: !emp.isActive,
      });
      toast({ title: emp.isActive ? 'Employee deactivated' : 'Employee activated' });
      await load();
    } catch (err) { toast({ title: err.message, variant: 'destructive' }); }
  };

  const departments = ['All', ...Array.from(new Set(employees.map(e => e.department))).sort()];
  const filtered = deptFilter === 'All' ? employees : employees.filter(e => e.department === deptFilter);
  const activeCount = employees.filter(e => e.isActive).length;
  const deptCount   = new Set(employees.map(e => e.department)).size;

  if (loading) return (
    <div className="space-y-6">
      <div><h1 className="text-3xl font-bold text-white">HR</h1></div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {[1,2,3].map(i => <Card key={i} className="bg-slate-800/50 border-slate-700 animate-pulse"><CardContent className="p-6 h-36"/></Card>)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">HR</h1>
          <p className="text-slate-400 mt-1">Employees, departments, and attendance</p>
        </div>
        {isHead && (
          <Button onClick={openAdd} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />Add Employee
          </Button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Employees', value: employees.length,  icon: Users,       color: 'text-blue-400',   bg: 'bg-blue-600/20'   },
          { label: 'Active',          value: activeCount,        icon: UserCheck,   color: 'text-green-400',  bg: 'bg-green-600/20'  },
          { label: 'Departments',     value: deptCount,          icon: Building2,   color: 'text-purple-400', bg: 'bg-purple-600/20' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <Card key={label} className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-9 h-9 ${bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                <Icon className={`h-4 w-4 ${color}`} />
              </div>
              <div><p className="text-xs text-slate-400">{label}</p><p className="text-xl font-bold text-white">{value}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Department filter */}
      {departments.length > 2 && (
        <div className="flex flex-wrap gap-2">
          {departments.map(d => (
            <button key={d} onClick={() => setDeptFilter(d)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                deptFilter === d ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
              }`}>
              {d}
            </button>
          ))}
        </div>
      )}

      {/* Employee list */}
      {filtered.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <Users className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Employees Yet</h3>
            <p className="text-slate-400 mb-4">Add your first employee to start tracking attendance.</p>
            {isHead && <Button onClick={openAdd} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-2" />Add Employee</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(emp => {
            const color = deptColor(emp.department);
            const isOpen = expanded === emp.id;
            return (
              <Card key={emp.id} className={`border-slate-700 overflow-hidden transition-all ${emp.isActive ? 'bg-slate-800/50' : 'bg-slate-800/20 opacity-60'}`}>
                {/* Row */}
                <div className="flex items-center gap-4 p-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${color}`}>
                    {emp.fullName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-semibold text-sm">{emp.fullName}</p>
                      <Badge className={`text-xs ${color} text-white border-0`}>{emp.department}</Badge>
                      {!emp.isActive && <Badge className="text-xs bg-red-900/50 text-red-400 border border-red-700/50">Inactive</Badge>}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-400">
                      {emp.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{emp.phone}</span>}
                      {emp.joinDate && <span>Joined {new Date(emp.joinDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isHead && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => openEdit(emp)} className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700 h-7 px-2.5 text-xs">
                          <Edit className="h-3 w-3 mr-1" />Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleToggleActive(emp)}
                          className={`h-7 px-2.5 text-xs ${emp.isActive ? 'border-red-600/50 text-red-400 hover:bg-red-600/20' : 'border-green-600/50 text-green-400 hover:bg-green-600/20'}`}>
                          {emp.isActive ? <><UserX className="h-3 w-3 mr-1" />Deactivate</> : <><UserCheck className="h-3 w-3 mr-1" />Activate</>}
                        </Button>
                      </>
                    )}
                    <button onClick={() => setExpanded(isOpen ? null : emp.id)}
                      className="flex items-center gap-1 text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-slate-700 transition-colors">
                      <Clock className="h-3.5 w-3.5" />
                      <span>Attendance</span>
                      {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                  </div>
                </div>

                {/* Attendance panel */}
                {isOpen && <AttendancePanel employee={emp} isHead={isHead} />}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add / Edit Employee Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingEmp ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingEmp ? 'Update employee details.' : 'Add a new employee to track attendance.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={form.fullName} onChange={setField('fullName')} className="bg-slate-700 border-slate-600" placeholder="e.g. Rafi Ahmed" required />
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Input value={form.department} onChange={setField('department')} className="bg-slate-700 border-slate-600" placeholder="e.g. Housekeeping, Kitchen, Front Desk" required />
            </div>
            <div className="space-y-2">
              <Label>Phone <span className="text-slate-500 font-normal text-xs">(optional)</span></Label>
              <Input value={form.phone} onChange={setField('phone')} className="bg-slate-700 border-slate-600" placeholder="+880 1XXX-XXXXXX" />
            </div>
            <div className="space-y-2">
              <Label>Join Date <span className="text-slate-500 font-normal text-xs">(optional)</span></Label>
              <Input type="date" value={form.joinDate} onChange={setField('joinDate')} className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)} className="border-slate-600">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                {editingEmp ? 'Update' : 'Add Employee'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HR;
